import React, { useState, useEffect } from 'react';
import { X, Building2, MapPin, Phone, CreditCard, Shield, Globe, Info, Save, Trash2, Plus } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const HospitalEditModal = ({ isOpen, onClose, hospital, onSuccess, defaultTab = 'general' }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  // Form states
  const [hospitalName, setHospitalName] = useState('');
  const [hospitalType, setHospitalType] = useState('Sole Proprietorship');
  const [otherHospitalType, setOtherHospitalType] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [issuingAuthority, setIssuingAuthority] = useState('');
  const [licenseValidity, setLicenseValidity] = useState('');
  const [description, setDescription] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');

  // Address
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [googleMapLocation, setGoogleMapLocation] = useState('');

  // Contact Person
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactDesignation, setContactDesignation] = useState('Admin');

  // Designated Employee
  const [hasDesignatedEmployee, setHasDesignatedEmployee] = useState(false);
  const [empName, setEmpName] = useState('');
  const [empRole, setEmpRole] = useState('');
  const [empPhone, setEmpPhone] = useState('');

  // Infrastructure
  const [totalBeds, setTotalBeds] = useState('');
  const [icuBeds, setIcuBeds] = useState('');
  const [otRooms, setOtRooms] = useState('');
  const [emergencyServices, setEmergencyServices] = useState(false);

  // Compliance
  const [nabhAccredited, setNabhAccredited] = useState(false);
  const [nabhNumber, setNabhNumber] = useState('');
  const [ayushmanBharatEmpanelled, setAyushmanBharatEmpanelled] = useState(false);
  const [ayushmanNumber, setAyushmanNumber] = useState('');

  // Financials
  const [panNumber, setPanNumber] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [gstBillingName, setGstBillingName] = useState('');
  const [platformFeePercent, setPlatformFeePercent] = useState(5);
  const [gstPercent, setGstPercent] = useState(18);

  // Bank details
  const [bankAccountName, setBankAccountName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankIfscCode, setBankIfscCode] = useState('');

  // Arrays as comma-separated strings
  const [specialties, setSpecialties] = useState('');
  const [facilities, setFacilities] = useState('');
  const [insuranceAccepted, setInsuranceAccepted] = useState('');

  // Set the tab whenever modal is opened
  useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab || 'general');
    }
  }, [isOpen, defaultTab]);

  // Pre-populate form when hospital prop changes
  useEffect(() => {
    if (hospital) {
      setHospitalName(hospital.hospitalName || '');
      setHospitalType(hospital.hospitalType || 'Sole Proprietorship');
      setOtherHospitalType(hospital.otherHospitalType || '');
      setRegistrationNumber(hospital.registrationNumber || '');
      setIssuingAuthority(hospital.issuingAuthority || '');
      setLicenseValidity(hospital.licenseValidity ? new Date(hospital.licenseValidity).toISOString().split('T')[0] : '');
      setDescription(hospital.description || '');
      setWebsiteUrl(hospital.websiteUrl || hospital.website || '');

      setStreet(hospital.address?.street || '');
      setCity(hospital.address?.city || '');
      setDistrict(hospital.address?.district || '');
      setState(hospital.address?.state || '');
      setPincode(hospital.address?.pincode || '');
      setGoogleMapLocation(hospital.address?.googleMapLocation || '');

      setContactName(hospital.contactPerson?.name || '');
      setContactPhone(hospital.contactPerson?.phone || '');
      setContactEmail(hospital.contactPerson?.email || '');
      setContactDesignation(hospital.contactPerson?.designation || 'Admin');

      setHasDesignatedEmployee(!!hospital.designatedEmployee?.name);
      setEmpName(hospital.designatedEmployee?.name || '');
      setEmpRole(hospital.designatedEmployee?.role || '');
      setEmpPhone(hospital.designatedEmployee?.phone || '');

      setTotalBeds(hospital.totalBeds !== undefined ? hospital.totalBeds : '');
      setIcuBeds(hospital.icuBeds !== undefined ? hospital.icuBeds : '');
      setOtRooms(hospital.otRooms !== undefined ? hospital.otRooms : '');
      setEmergencyServices(!!hospital.emergencyServices);

      setNabhAccredited(!!hospital.nabhAccredited);
      setNabhNumber(hospital.nabhNumber || '');
      setAyushmanBharatEmpanelled(!!hospital.ayushmanBharatEmpanelled);
      setAyushmanNumber(hospital.ayushmanNumber || '');

      setPanNumber(hospital.panNumber || '');
      setGstNumber(hospital.gstNumber || '');
      setGstBillingName(hospital.gstBillingName || '');
      setPlatformFeePercent(hospital.platformFeePercent !== undefined ? hospital.platformFeePercent : 5);
      setGstPercent(hospital.gstPercent !== undefined ? hospital.gstPercent : 18);

      setBankAccountName(hospital.bankDetails?.accountName || '');
      setBankAccountNumber(hospital.bankDetails?.accountNumber || '');
      setBankName(hospital.bankDetails?.bankName || '');
      setBankIfscCode(hospital.bankDetails?.ifscCode || '');

      setSpecialties(hospital.specialties?.join(', ') || '');
      setFacilities(hospital.facilities?.join(', ') || '');
      setInsuranceAccepted(hospital.insuranceAccepted?.join(', ') || '');
    }
  }, [hospital, isOpen]);

  if (!isOpen || !hospital) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        hospitalName,
        hospitalType,
        otherHospitalType: hospitalType === 'Others' ? otherHospitalType : undefined,
        registrationNumber,
        issuingAuthority,
        licenseValidity: licenseValidity || null,
        description,
        websiteUrl,
        address: {
          street,
          city,
          district,
          state,
          pincode,
          googleMapLocation
        },
        contactPerson: {
          name: contactName,
          phone: contactPhone,
          email: contactEmail,
          designation: contactDesignation
        },
        designatedEmployee: hasDesignatedEmployee ? {
          name: empName,
          role: empRole,
          phone: empPhone
        } : null,
        totalBeds: totalBeds !== '' ? Number(totalBeds) : undefined,
        icuBeds: icuBeds !== '' ? Number(icuBeds) : undefined,
        otRooms: otRooms !== '' ? Number(otRooms) : undefined,
        emergencyServices,
        nabhAccredited,
        nabhNumber: nabhAccredited ? nabhNumber : '',
        ayushmanBharatEmpanelled,
        ayushmanNumber: ayushmanBharatEmpanelled ? ayushmanNumber : '',
        panNumber,
        gstNumber,
        gstBillingName,
        platformFeePercent: Number(platformFeePercent),
        gstPercent: Number(gstPercent),
        bankDetails: {
          accountName: bankAccountName,
          accountNumber: bankAccountNumber,
          bankName,
          ifscCode: bankIfscCode
        },
        specialties: specialties ? specialties.split(',').map(s => s.trim()).filter(Boolean) : [],
        facilities: facilities ? facilities.split(',').map(f => f.trim()).filter(Boolean) : [],
        insuranceAccepted: insuranceAccepted ? insuranceAccepted.split(',').map(i => i.trim()).filter(Boolean) : []
      };

      const response = await api.put(`/hospitals/${hospital._id}`, payload);
      if (response.data) {
        toast.success('Hospital profile updated successfully!');
        onSuccess();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update hospital');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'General Info', icon: Building2 },
    { id: 'address', label: 'Location', icon: MapPin },
    { id: 'contact', label: 'Contacts', icon: Phone },
    { id: 'finance', label: 'Financials & KYC', icon: CreditCard },
    { id: 'compliance', label: 'Infrastructure & Accreds', icon: Shield }
  ];

  const ActiveIcon = tabs.find(t => t.id === activeTab)?.icon || Building2;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
      <div className="bg-white w-full max-w-5xl h-[95vh] md:h-[90vh] overflow-hidden rounded-[32px] md:rounded-[40px] shadow-2xl flex flex-col relative animate-in fade-in zoom-in duration-300">
        
        {/* Header */}
        <div className="px-6 py-5 md:px-10 md:py-6 border-b border-slate-100 flex items-center justify-between bg-white relative z-10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 shadow-inner shrink-0">
              <ActiveIcon className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-black text-[#143E78] tracking-tight uppercase">
                Edit Hospital Profile
              </h2>
              <p className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">
                Update credentials, contacts & capacity settings
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-full transition-all text-slate-400">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tab switcher */}
        <div className="px-6 md:px-10 bg-slate-50 border-b border-slate-100 flex gap-2 overflow-x-auto py-2 shrink-0">
          {tabs.map(tab => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 text-[10px] md:text-xs font-black uppercase tracking-wider rounded-xl transition-all ${
                  isActive ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200/50 hover:text-slate-700'
                }`}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Form content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 md:px-10 md:py-8 space-y-6">
          
          {/* GENERAL INFO TAB */}
          {activeTab === 'general' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Hospital Name</label>
                  <input
                    type="text"
                    required
                    value={hospitalName}
                    onChange={(e) => setHospitalName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Hospital Type</label>
                  <select
                    value={hospitalType}
                    onChange={(e) => setHospitalType(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  >
                    <option value="Sole Proprietorship">Sole Proprietorship</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Private Limited">Private Limited</option>
                    <option value="LLP">LLP</option>
                    <option value="Trust">Trust</option>
                    <option value="Others">Others</option>
                  </select>
                </div>
                {hospitalType === 'Others' && (
                  <div className="space-y-1 md:col-span-2">
                    <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Specify Hospital Type</label>
                    <input
                      type="text"
                      required
                      value={otherHospitalType}
                      onChange={(e) => setOtherHospitalType(e.target.value)}
                      placeholder="e.g. Foundation, Government Clinic"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                    />
                  </div>
                )}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Registration Number</label>
                  <input
                    type="text"
                    required
                    value={registrationNumber}
                    onChange={(e) => setRegistrationNumber(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Issuing Authority</label>
                  <input
                    type="text"
                    value={issuingAuthority}
                    onChange={(e) => setIssuingAuthority(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">License Validity Date</label>
                  <input
                    type="date"
                    value={licenseValidity}
                    onChange={(e) => setLicenseValidity(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all cursor-pointer"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Website URL</label>
                  <input
                    type="text"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all min-h-[80px]"
                  />
                </div>
              </div>
            </div>
          )}

          {/* LOCATION TAB */}
          {activeTab === 'address' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1 md:col-span-2">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Street Address</label>
                  <input
                    type="text"
                    required
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">City</label>
                  <input
                    type="text"
                    required
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">District</label>
                  <input
                    type="text"
                    value={district}
                    onChange={(e) => setDistrict(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">State</label>
                  <input
                    type="text"
                    required
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Pincode</label>
                  <input
                    type="text"
                    required
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Google Maps Location Link</label>
                  <input
                    type="text"
                    value={googleMapLocation}
                    onChange={(e) => setGoogleMapLocation(e.target.value)}
                    placeholder="e.g. https://maps.google.com/..."
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>
            </div>
          )}

          {/* CONTACTS TAB */}
          {activeTab === 'contact' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
              <h3 className="text-xs font-black text-[#143E78] uppercase tracking-widest border-b pb-2">Primary Contact Person</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Full Name</label>
                  <input
                    type="text"
                    required
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Phone Number</label>
                  <input
                    type="text"
                    required
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Email Address</label>
                  <input
                    type="email"
                    required
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Designation</label>
                  <input
                    type="text"
                    value={contactDesignation}
                    onChange={(e) => setContactDesignation(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>

              <div className="mt-8 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="editHasDesignatedEmployee"
                    checked={hasDesignatedEmployee}
                    onChange={(e) => setHasDesignatedEmployee(e.target.checked)}
                    className="w-4 h-4 text-[#143E78] rounded border-slate-300 focus:ring-[#143E78]"
                  />
                  <label htmlFor="editHasDesignatedEmployee" className="text-xs font-black text-[#143E78] uppercase tracking-widest cursor-pointer">
                    Add Designated Employee (Optional)
                  </label>
                </div>

                {hasDesignatedEmployee && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-6 rounded-2xl border border-slate-100 animate-in fade-in duration-200">
                    <div className="space-y-1">
                      <label className="text-[9px] font-bold text-slate-500 uppercase">Employee Name</label>
                      <input
                        type="text"
                        required
                        value={empName}
                        onChange={(e) => setEmpName(e.target.value)}
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-slate-800 text-sm outline-none focus:border-indigo-500"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-bold text-slate-500 uppercase">Job Role</label>
                      <input
                        type="text"
                        required
                        value={empRole}
                        onChange={(e) => setEmpRole(e.target.value)}
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-slate-800 text-sm outline-none focus:border-indigo-500"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-bold text-slate-500 uppercase">WhatsApp Number</label>
                      <input
                        type="text"
                        required
                        value={empPhone}
                        onChange={(e) => setEmpPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-slate-800 text-sm outline-none focus:border-indigo-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* FINANCIALS TAB */}
          {activeTab === 'finance' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
              <h3 className="text-xs font-black text-[#143E78] uppercase tracking-widest border-b pb-2">KYC Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">PAN Number</label>
                  <input
                    type="text"
                    required
                    value={panNumber}
                    onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">GST Number (Optional)</label>
                  <input
                    type="text"
                    value={gstNumber}
                    onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">GST Billing Name (Optional)</label>
                  <input
                    type="text"
                    value={gstBillingName}
                    onChange={(e) => setGstBillingName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>

              <h3 className="text-xs font-black text-[#143E78] uppercase tracking-widest border-b pb-2 mt-8">Payment Config %</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Platform Fee Percent (%)</label>
                  <input
                    type="number"
                    required
                    value={platformFeePercent}
                    onChange={(e) => setPlatformFeePercent(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">GST Percent (%)</label>
                  <input
                    type="number"
                    required
                    value={gstPercent}
                    onChange={(e) => setGstPercent(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>

              <h3 className="text-xs font-black text-[#143E78] uppercase tracking-widest border-b pb-2 mt-8">Bank Account details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Account Name</label>
                  <input
                    type="text"
                    required
                    value={bankAccountName}
                    onChange={(e) => setBankAccountName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Account Number</label>
                  <input
                    type="text"
                    required
                    value={bankAccountNumber}
                    onChange={(e) => setBankAccountNumber(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Bank Name</label>
                  <input
                    type="text"
                    required
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">IFSC Code</label>
                  <input
                    type="text"
                    required
                    value={bankIfscCode}
                    onChange={(e) => setBankIfscCode(e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>
            </div>
          )}

          {/* COMPLIANCE & CAPACITY TAB */}
          {activeTab === 'compliance' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
              <h3 className="text-xs font-black text-[#143E78] uppercase tracking-widest border-b pb-2">Infrastructure & Capacity</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Total Beds</label>
                  <input
                    type="number"
                    value={totalBeds}
                    onChange={(e) => setTotalBeds(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">ICU Beds</label>
                  <input
                    type="number"
                    value={icuBeds}
                    onChange={(e) => setIcuBeds(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">OT Rooms</label>
                  <input
                    type="number"
                    value={otRooms}
                    onChange={(e) => setOtRooms(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1 flex flex-col justify-end pb-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="editEmergencyServices"
                      checked={emergencyServices}
                      onChange={(e) => setEmergencyServices(e.target.checked)}
                      className="w-4 h-4 text-[#143E78] rounded border-slate-300 focus:ring-[#143E78] cursor-pointer"
                    />
                    <label htmlFor="editEmergencyServices" className="text-[10px] font-black text-[#143E78] uppercase tracking-widest cursor-pointer">
                      Emergency Services
                    </label>
                  </div>
                </div>
              </div>

              <h3 className="text-xs font-black text-[#143E78] uppercase tracking-widest border-b pb-2 mt-8">Licensing & Accreditations</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-6 rounded-2xl border border-slate-100">
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="editNabhAccredited"
                      checked={nabhAccredited}
                      onChange={(e) => setNabhAccredited(e.target.checked)}
                      className="w-4 h-4 text-[#143E78] rounded border-slate-300 focus:ring-[#143E78] cursor-pointer"
                    />
                    <label htmlFor="editNabhAccredited" className="text-[10px] font-black text-[#143E78] uppercase tracking-widest cursor-pointer">
                      NABH Accredited
                    </label>
                  </div>
                  {nabhAccredited && (
                    <div className="space-y-1 animate-in fade-in duration-200">
                      <label className="text-[9px] font-bold text-slate-500 uppercase">NABH Certification Number</label>
                      <input
                        type="text"
                        required
                        value={nabhNumber}
                        onChange={(e) => setNabhNumber(e.target.value)}
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-slate-800 text-sm outline-none focus:border-indigo-500"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="editAyushmanEmpanelled"
                      checked={ayushmanBharatEmpanelled}
                      onChange={(e) => setAyushmanBharatEmpanelled(e.target.checked)}
                      className="w-4 h-4 text-[#143E78] rounded border-slate-300 focus:ring-[#143E78] cursor-pointer"
                    />
                    <label htmlFor="editAyushmanEmpanelled" className="text-[10px] font-black text-[#143E78] uppercase tracking-widest cursor-pointer">
                      Ayushman Bharat Empanelled
                    </label>
                  </div>
                  {ayushmanBharatEmpanelled && (
                    <div className="space-y-1 animate-in fade-in duration-200">
                      <label className="text-[9px] font-bold text-slate-500 uppercase">Ayushman Bharat ID Number</label>
                      <input
                        type="text"
                        required
                        value={ayushmanNumber}
                        onChange={(e) => setAyushmanNumber(e.target.value)}
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-slate-800 text-sm outline-none focus:border-indigo-500"
                      />
                    </div>
                  )}
                </div>
              </div>

              <h3 className="text-xs font-black text-[#143E78] uppercase tracking-widest border-b pb-2 mt-8">Tags & Groupings (Comma-separated values)</h3>
              <div className="grid grid-cols-1 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Specialties</label>
                  <input
                    type="text"
                    value={specialties}
                    onChange={(e) => setSpecialties(e.target.value)}
                    placeholder="e.g. Cardiology, Orthopedics, Pediatrics"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Facilities</label>
                  <input
                    type="text"
                    value={facilities}
                    onChange={(e) => setFacilities(e.target.value)}
                    placeholder="e.g. 24/7 Ambulance, Pharmacy, Cafeteria"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Insurances Accepted</label>
                  <input
                    type="text"
                    value={insuranceAccepted}
                    onChange={(e) => setInsuranceAccepted(e.target.value)}
                    placeholder="e.g. Star Health, HDFC Ergo, Niva Bupa"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-5 md:px-10 md:py-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 rounded-b-[32px] md:rounded-b-[40px] shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 rounded-xl transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2.5 bg-[#143E78] text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-indigo-900 transition-all shadow-lg active:scale-95 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : 'Save Updates'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default HospitalEditModal;
